import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import portalApi, { PORTAL_PATIENT_KEY, PORTAL_MEMBER_KEY, getActiveMemberId, setActiveMemberHeader } from './lib/portalApi';

const PortalAuthContext = createContext(null);

export function PortalAuthProvider({ children }) {
  const [patient, setPatient] = useState(() => {
    try { return JSON.parse(localStorage.getItem(PORTAL_PATIENT_KEY) || 'null'); }
    catch { return null; }
  });
  const [loading, setLoading] = useState(true);
  const [favourites, setFavourites] = useState({ pharmacy_ids: [], branch_ids: [], preferred_branch_id: null, pharmacies: [], branches: [] });
  const [favouritesSaving, setFavouritesSaving] = useState(false);
  // Family profiles — the guardian can act for a dependant on every portal screen.
  const [family, setFamily] = useState([]);
  const [activeMemberId, setActiveMemberIdState] = useState(() => getActiveMemberId());

  // The token itself now only ever lives in the httpOnly `portal_session`
  // cookie the backend sets on the login/signup response — we just cache
  // the (non-sensitive) display doc for instant paint on next visit.
  const persist = useCallback((patientDoc) => {
    if (patientDoc) {
      localStorage.setItem(PORTAL_PATIENT_KEY, JSON.stringify(patientDoc));
      setPatient(patientDoc);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const res = await portalApi.get('/me');
      setPatient(res.data);
      localStorage.setItem(PORTAL_PATIENT_KEY, JSON.stringify(res.data));
      return res.data;
    } catch {
      setPatient(null);
      localStorage.removeItem(PORTAL_PATIENT_KEY);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash.includes('session_id=')) { setLoading(false); return; }
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!patient?.patient_user_id) {
      setFavourites({ pharmacy_ids: [], branch_ids: [], preferred_branch_id: null, pharmacies: [], branches: [] });
      return;
    }
    portalApi.get('/favourites').then(response => setFavourites(response.data)).catch(() => {});
  }, [patient?.patient_user_id]);

  const refreshFamily = useCallback(async () => {
    try {
      const response = await portalApi.get('/family');
      const members = response.data.members || [];
      setFamily(members);
      // A removed or converted member can never stay selected.
      setActiveMemberIdState(current => {
        if (current && !members.some(m => m.patient_user_id === current)) {
          setActiveMemberHeader('');
          return '';
        }
        return current;
      });
      return members;
    } catch { return []; }
  }, []);

  useEffect(() => {
    if (!patient?.patient_user_id) {
      setFamily([]);
      setActiveMemberHeader('');
      setActiveMemberIdState('');
      return;
    }
    refreshFamily();
  }, [patient?.patient_user_id, refreshFamily]);

  const setActiveMember = useCallback((memberId) => {
    setActiveMemberHeader(memberId || '');
    setActiveMemberIdState(memberId || '');
  }, []);

  const activeMember = family.find(m => m.patient_user_id === activeMemberId) || null;

  const saveFavourites = useCallback(async (next) => {
    setFavouritesSaving(true);
    try {
      const response = await portalApi.put('/favourites', {
        pharmacy_ids: next.pharmacy_ids || [], branch_ids: next.branch_ids || [],
        preferred_branch_id: next.preferred_branch_id || null,
      });
      setFavourites(response.data);
      setPatient(current => current ? { ...current, preferred_tenant_id: response.data.preferred_branch_id } : current);
      return response.data;
    } finally { setFavouritesSaving(false); }
  }, []);

  const login = async ({ email, phone, password }) => {
    const res = await portalApi.post('/auth/login', { email, phone, password });
    persist(res.data.patient);
    return res.data.patient;
  };

  const signup = async (payload) => {
    const res = await portalApi.post('/auth/signup', payload);
    persist(res.data.patient);
    return res.data.patient;
  };

  const requestOtp = async (phone) => {
    const res = await portalApi.post('/auth/otp/request', { phone });
    return res.data;
  };

  const verifyOtp = async (payload) => {
    const res = await portalApi.post('/auth/otp/verify', payload);
    persist(res.data.patient);
    return res.data.patient;
  };

  const googleSession = async (session_id, preferred_tenant_id) => {
    const res = await portalApi.post('/auth/google/session', { session_id, preferred_tenant_id });
    persist(res.data.patient);
    return res.data.patient;
  };

  const forgotPassword = async (email) => {
    const res = await portalApi.post('/auth/forgot-password', { email });
    return res.data;
  };

  const resetPassword = async ({ email, code, newPassword }) => {
    const res = await portalApi.post('/auth/reset-password', { email, code, new_password: newPassword });
    persist(res.data.patient);
    return res.data.patient;
  };

  const requestMagicLink = async (email, login_path) => {
    const res = await portalApi.post('/auth/magic-link', { email, login_path });
    return res.data;
  };

  const verifyMagicLink = async (token) => {
    const res = await portalApi.post('/auth/magic-link/verify', { token });
    persist(res.data.patient);
    return res.data.patient;
  };

  const logout = async () => {
    try { await portalApi.post('/auth/logout'); } catch { /* ignore */ }
    localStorage.removeItem(PORTAL_PATIENT_KEY);
    localStorage.removeItem(PORTAL_MEMBER_KEY);
    setActiveMemberHeader('');
    setActiveMemberIdState('');
    setFamily([]);
    setPatient(null);
    setFavourites({ pharmacy_ids: [], branch_ids: [], preferred_branch_id: null, pharmacies: [], branches: [] });
  };

  return (
    <PortalAuthContext.Provider value={{
      patient, loading, refresh, favourites, favouritesSaving, saveFavourites,
      family, refreshFamily, activeMemberId, activeMember, setActiveMember,
      login, signup, requestOtp, verifyOtp, googleSession, forgotPassword, resetPassword, logout,
      requestMagicLink, verifyMagicLink,
    }}>
      {children}
    </PortalAuthContext.Provider>
  );
}

export const usePortalAuth = () => {
  const ctx = useContext(PortalAuthContext);
  if (!ctx) throw new Error('usePortalAuth must be used inside PortalAuthProvider');
  return ctx;
};
